import React, { useEffect, useState } from 'react'

import { AsyncButton } from 'components/Button'
import { Title, Subtitle } from 'components/IndexLayout'
import Spinner from 'components/Spinner'
import Pluralize from 'components/Pluralize'

import { PlanPicker } from '../../components'
import { BillingPlanFragment, useDraftBillingSummaryQuery } from 'gqlTypes'

import { formatPriceShort } from 'utils/money'

import {
  EstimatedPriceText,
  PlanPickerWrapper,
  Wrapper,
  ErrorMessage,
} from './WorkspaceSubscribePlan.styles'

const PLAN_PERIODICITY = {
  MONTHLY: 'monthly',
  YEARLY: 'yearly',
  TRIAL: '' /* This Plan shouldn't exist on FE */,
}

export interface WorkspaceSubscribePlanProps {
  selectedPlan?: BillingPlanFragment
  plans: BillingPlanFragment[]
  numberOfEditorSeats: number
  onPaymentDetails: (plan: BillingPlanFragment) => Promise<void>
}

const formatPrice = (price: number) =>
  formatPriceShort(price, { trimDecimalIfZero: false })

export const WorkspaceSubscribePlan: React.FC<WorkspaceSubscribePlanProps> = props => {
  const { selectedPlan, plans, onPaymentDetails, numberOfEditorSeats } = props
  const [currentPlan, setPlan] = useState(selectedPlan)

  // Make sure the "selectedPlan" is always synced
  // with the currentPlan
  useEffect(() => {
    setPlan(selectedPlan)
  }, [selectedPlan])

  const { data, loading } = useDraftBillingSummaryQuery({
    variables: {
      planId: currentPlan?.id || '',
      seats: numberOfEditorSeats,
    },
    skip: !currentPlan,
  })

  const editorCost = data
    ? `${data.draftBillingSummary.billingCycleAmount}` // Type casting to a string to avoid 0 being falsy
    : undefined
  const planPeriodicity = currentPlan?.type
    ? PLAN_PERIODICITY[currentPlan!.type]
    : ''

  const isDisabled = !currentPlan || editorCost === '0'

  return (
    <>
      <Wrapper>
        <Title>Choose a Plan</Title>
        <Subtitle>You only pay for users who edit Sketch documents.</Subtitle>

        <PlanPickerWrapper>
          <PlanPicker
            plans={plans}
            onChange={plan => setPlan(plan)}
            selectedPlan={currentPlan}
            isDisabled={editorCost === '0'}
          />
        </PlanPickerWrapper>

        <EstimatedPriceText>
          {!loading ? (
            editorCost !== '0' ? (
              <p>
                Based on your <b>{numberOfEditorSeats} Editor</b>{' '}
                <Pluralize
                  singular="Seat"
                  plural="Seats"
                  count={numberOfEditorSeats}
                  wrapperElement={React.Fragment}
                />
                , your projected total {planPeriodicity} bill will be{' '}
                <b>{formatPrice(Number(editorCost))}</b> plus tax (if
                applicable).
              </p>
            ) : (
              <ErrorMessage>
                Something went wrong while loading your details. Please contact
                Sketch Customer Support.
              </ErrorMessage>
            )
          ) : (
            <Spinner.Flex primary />
          )}
        </EstimatedPriceText>
      </Wrapper>

      <AsyncButton
        variant="primary"
        fill
        onClick={async () => {
          await onPaymentDetails(currentPlan!)
        }}
        disabled={isDisabled}>
        <>Payment Details</>
      </AsyncButton>
    </>
  )
}
